import userflow, { type Attributes } from 'userflow.js';
import {
  firstValueFrom,
  Observable,
  ReplaySubject,
} from 'rxjs';
import { Inject, Injectable } from '@angular/core';
import {
  ExportToken,
  LogService,
  TOKENS as COMMON_TOKENS,
} from '@trustedshops/tswp-core-common';
import {
  TOKENS,
  UserFlowInitData,
  UserFlowSafeMethods,
  UserFlowService,
} from '@trustedshops/tswp-core-masterdata';

@Injectable()
@ExportToken(TOKENS.UserFlowService)
export class UserflowServiceImpl implements UserFlowService {
  /** Service type identifier for logging purposes */
  private static readonly TYPE = 'UserflowServiceImpl';

  /** Private reference to the UserFlow SDK instance */
  readonly #userFlow = userflow;

  /**
   * ReplaySubject tracking the initialization state of the UserFlow service.
   * Emits true when successfully initialized, false when failed.
   * Replays the last value to late subscribers.
   */
  private readonly initializationState = new ReplaySubject<boolean>(1);

  /**
   * Private flag to track if initialization has been attempted to prevent duplicate calls.
   */
  #isInitialized = false;

  /**
   * Creates an instance of UserflowServiceImpl.
   *
   * @param logService - Injected logging service for debugging and error reporting
   */
  constructor(
    @Inject(COMMON_TOKENS.LogService)
    private readonly logService: LogService
  ) {}

  /**
   * Gets an Observable stream of the initialization status.
   *
   * @returns Observable that emits true when service is ready, false when failed
   */
  get isReady$(): Observable<boolean> {
    return this.initializationState.asObservable();
  }

  /**
   * Gets the UserFlow SDK instance with safe methods.
   * Only use this after confirming the service is ready via `isReady`.
   *
   * @returns UserFlow SDK instance with safe method access
   */
  get userflow(): UserFlowSafeMethods {
    return this.#userFlow;
  }

  /**
   * Initializes the UserFlow service with API key, user identification, and channel grouping.
   *
   * This method:
   * 1. Checks if already initialized to prevent duplicate initialization
   * 2. Initializes UserFlow with the provided API key
   * 3. Identifies the user with their attributes
   * 4. Groups the user into the specified channel
   * 5. Updates the initialization state
   *
   * @template User - Type extending Attributes for user data
   * @template Channel - Type extending Attributes for channel data
   * @param config - Configuration object containing API key, user, and channel data
   * @param config.apiKey - UserFlow API key for authentication
   * @param config.user - User object containing identityRef and additional attributes
   * @param config.channel - Channel object containing channelId and additional attributes
   * @returns Promise that resolves when initialization is complete
   *
   * @throws Will log errors but not throw exceptions - check `isReady` to verify success (returns true/false)
   */
  public async init<User extends Attributes, Channel extends Attributes>({
    apiKey,
    user,
    channel,
  }: UserFlowInitData<User, Channel>): Promise<void> {
    // Prevent duplicate initialization - we need to track this separately since Subject doesn't have getValue()
    if (this.#isInitialized) {
      this.logService.debug(
        UserflowServiceImpl.TYPE,
        'UserFlow already initialized, skipping duplicate initialization'
      );
      return;
    }

    // Mark that we've attempted initialization
    this.#isInitialized = true;

    try {
      // Initialize UserFlow SDK with API key
      this.#userFlow.init(apiKey);

      // Identify the user - identityRef is required for user identification
      await this.#userFlow.identify(user['identityRef'] as string, { ...user });

      // Group user into channel - channelId is required for channel grouping
      await this.#userFlow.group(channel['channelId'] as string, {
        ...channel,
      });

      // Mark as successfully initialized
      this.initializationState.next(true);

      this.logService.debug(
        UserflowServiceImpl.TYPE,
        'UserFlow initialized successfully'
      );
    } catch (error: unknown) {
      // Mark initialization as failed
      this.initializationState.next(false);

      // Log the error and mark initialization as failed
      this.logError('UserFlow initialization failed', error);
    }
  }

  /**
   * Gets a Promise of the initialization status.
   *
   * @returns Promise that resolves to true when service is ready, false when failed
   */
  public isReady(): Promise<boolean> {
    return firstValueFrom(this.isReady$);
  }

  /**
   * Logs error messages with optional error details.
   *
   * Handles both simple error messages and errors with additional context.
   * Error objects are serialized to JSON for better debugging information.
   *
   * @param message - Primary error message describing what failed
   * @param error - Optional error object or additional error details
   * @private
   */
  private logError(message: string, error?: unknown): void {
    const errorMessage = error
      ? `${message}: ${JSON.stringify(error)}`
      : message;

    this.logService.error(UserflowServiceImpl.TYPE, new Error(errorMessage));
  }
}
